import {Dispatch, MutableRefObject, SetStateAction, useEffect, useRef} from "react"
import {I18nIndividual as GedcomIndividual} from "../model/gedcom-individual"
import {I18nFamily as GedcomFamily} from "../model/gedcom-family"
import {getStorageSize, restoreState, storeDeltaState, storeState} from "../utils/storage-utils"
import * as Sentry from "@sentry/react"

/**
 * Persist individuals and families to session/local storage with delta storage.
 */
export function useSessionStorage(
    from: string,
    lastIndividualId: MutableRefObject<number>,
    individualsData: GedcomIndividual[],
    setIndividualsData: Dispatch<SetStateAction<GedcomIndividual[]>>,
    lastFamilyId: MutableRefObject<number>,
    familiesData: GedcomFamily[],
    setFamiliesData: Dispatch<SetStateAction<GedcomFamily[]>>
) {
    const restoringInProgress = useRef(true)

    // Last saved state to compute delta
    const lastSavedIndividuals = useRef<GedcomIndividual[]>([])
    const lastSavedFamilies = useRef<GedcomFamily[]>([])

    // Recent state references for beforeunload
    const recentIndividuals = useRef(individualsData)
    const recentFamilies = useRef(familiesData)

    // Keep refs updated
    useEffect(() => { recentIndividuals.current = individualsData }, [individualsData])
    useEffect(() => { recentFamilies.current = familiesData }, [familiesData])

    /**
     * Restore state from session/local storage.
     */
    useEffect(() => {
        if (!from) restoreState(lastIndividualId, setIndividualsData, lastFamilyId, setFamiliesData)
        restoringInProgress.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    /**
     * Store delta state whenever new rows are added.
     * Avoids triggering on every minor edit
     */
    useEffect(() => {
        if (!restoringInProgress.current) {
            storeDeltaState(
                lastIndividualId, individualsData, lastSavedIndividuals,
                lastFamilyId, familiesData, lastSavedFamilies
            )
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [individualsData.length, familiesData.length])  // Trigger only on row length changes

    /**
     * Debounced batch storage for edits to existing rows.
     */
    useEffect(() => {
        if (restoringInProgress.current) return
        const timeout = setTimeout(() => {
            storeDeltaState(
                lastIndividualId, individualsData, lastSavedIndividuals,
                lastFamilyId, familiesData, lastSavedFamilies
            )
        }, 500)
        return () => clearTimeout(timeout)
    }, [lastIndividualId, individualsData, lastFamilyId, familiesData])

    /**
     * Persist full state on page reload/unload.
     */
    useEffect(() => {
        const handleBeforeUnload = (_: BeforeUnloadEvent) =>
            storeState(localStorage, recentIndividuals, lastIndividualId, recentFamilies, lastFamilyId)
        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => window.removeEventListener("beforeunload", handleBeforeUnload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])  // Run only once

    /**
     * Warn if localStorage size grows too large
     */
    useEffect(() => {
        const sizeMB = getStorageSize(localStorage)
        const thresholdMB = 5
        if (sizeMB > thresholdMB) {
            console.warn(`Local storage getting chubby: ${sizeMB.toFixed(2)}MB`)
            Sentry.captureMessage("Local storage getting chubby", { level: "log", extra: {
                storageSize: `${sizeMB.toFixed(2)}MB`
            }})
        }
    }, [individualsData.length, familiesData.length])
}
